import type { DashboardStats, QuickAction } from '@/services/dashboard.service';
import type { User } from '@/stores';
import { normalizePermissionClaims } from '@/features/auth/utils/permission-matcher';
import { getRoleDisplayName } from '@/features/auth/utils';

export interface DashboardStatsViewModel {
  totalEmployees: number;
  activeEmployees: number;
  presentToday: number;
  onLeave: number;
  inactive: number;
  monthlyPayroll: number;
  avgSalary: number;
  newHires: number;
  pendingLeaves: number;
  hasReportees: boolean;
}

export interface DashboardUserContext extends Partial<User> {
  departmentName?: string;
  departmentCode?: string;
  positionTitle?: string;
  positionCode?: string;
  isChiefPosition?: boolean;
  teamIds?: string[];
  managedTeamIds?: string[];
  managedDepartmentId?: string;
}

export interface DashboardAccessContext {
  roleLabel: string;
  departmentLabel: string;
  positionLabel: string;
  scopeLabel: string;
  scopeDescription: string;
  permissionSummary: {
    label: string;
    shouldList: boolean;
    preview: string[];
  };
}

export interface DashboardSelfWorkspace {
  kind: 'governance' | 'employee' | 'account';
  title: string;
  description: string;
  actionLabel: string;
  href: '/attendance/regularization' | '/employees/me' | '/settings/profile';
}

export type OrganizationSetupSection = 'profile' | 'regional' | 'departments' | 'teams';
export type DashboardAudience = 'tenant' | 'managed' | 'self';

const MANAGED_EMPLOYEE_READ_PERMISSIONS = [
  'hrms:employee:read:team',
  'hrms:employee:read:department',
] as const;

const ORGANIZATION_SETUP_PERMISSIONS: Record<OrganizationSetupSection, string[]> = {
  profile: ['tenant:settings:update:tenant', 'admin:tenant:update:global'],
  regional: ['tenant:settings:update:tenant', 'admin:tenant:update:global'],
  departments: [
    'tenant:settings:update:tenant',
    'admin:tenant:update:global',
    'hrms:department:create:tenant',
    'hrms:department:update:tenant',
    'hrms:department:delete:tenant',
  ],
  teams: [
    'tenant:settings:update:tenant',
    'admin:tenant:update:global',
    'hrms:team:create:tenant',
    'hrms:team:update:tenant',
    'hrms:team:delete:tenant',
  ],
};

export function buildDashboardStats(stats?: DashboardStats): DashboardStatsViewModel {
  const totalEmployees = stats?.totalEmployees ?? 0;
  const activeEmployees = stats?.activeEmployees ?? totalEmployees;
  const onLeave = stats?.onLeave ?? 0;
  const inactive = stats?.inactiveEmployees ?? Math.max(0, totalEmployees - activeEmployees);

  return {
    totalEmployees,
    activeEmployees,
    presentToday: stats?.presentToday ?? 0,
    onLeave,
    inactive,
    monthlyPayroll: stats?.monthlyPayroll ?? 0,
    avgSalary: stats?.avgSalary ?? 0,
    newHires: stats?.recentHires ?? 0,
    pendingLeaves: stats?.pendingApprovals ?? 0,
    hasReportees: stats?.hasReportees ?? true,
  };
}

export function filterVisibleQuickActions(
  actions: QuickAction[],
  hasPermission: (permission: string) => boolean,
): QuickAction[] {
  return actions.filter((action) => hasPermission(action.permission));
}

export function getVisibleOrganizationSetupSections(
  hasPermission: (permission: string) => boolean,
): OrganizationSetupSection[] {
  return (Object.keys(ORGANIZATION_SETUP_PERMISSIONS) as OrganizationSetupSection[]).filter(
    (section) => ORGANIZATION_SETUP_PERMISSIONS[section].some(hasPermission),
  );
}

export function getDashboardAudience(
  hasPermission: (permission: string) => boolean,
): DashboardAudience {
  if (hasPermission('hrms:dashboard:read:tenant')) {
    return 'tenant';
  }

  if (MANAGED_EMPLOYEE_READ_PERMISSIONS.some(hasPermission)) {
    return 'managed';
  }

  return 'self';
}

function isAttendanceGovernanceApprover(user: DashboardUserContext | null): boolean {
  return (
    String(user?.role ?? '')
      .trim()
      .toLowerCase() === 'attendance_governance_approver'
  );
}

export function getDashboardSelfWorkspace(
  user: DashboardUserContext | null,
  hasPermission: (permission: string) => boolean,
): DashboardSelfWorkspace {
  if (isAttendanceGovernanceApprover(user)) {
    return {
      kind: 'governance',
      title: 'Governance Workspace',
      description:
        'Review attendance corrections explicitly routed to you. This account is intentionally separate from an employee profile.',
      actionLabel: 'Open Assigned Approvals',
      href: '/attendance/regularization',
    };
  }

  if (user?.employeeId && hasPermission('hrms:employee:read:self')) {
    return {
      kind: 'employee',
      title: 'Self-Service Dashboard',
      description:
        'Tenant-wide workforce metrics are hidden for your role. Use your self-service pages for profile, leave, holidays, shifts, payslips, and notifications.',
      actionLabel: 'Open My Profile',
      href: '/employees/me',
    };
  }

  return {
    kind: 'account',
    title: 'Account Workspace',
    description:
      'This account is not linked to an employee profile. Use account settings to manage your sign-in and security preferences.',
    actionLabel: 'Open My Account',
    href: '/settings/profile',
  };
}

export function buildAccessContext(user: DashboardUserContext | null): DashboardAccessContext {
  const permissions = normalizePermissionClaims(user?.permissions ?? []);
  const isTenantAdmin = user?.role === 'TENANT_ADMIN';
  const isPlatformAdmin = user?.role === 'SUPER_ADMIN';
  const normalizedRole = String(user?.role ?? '')
    .trim()
    .toLowerCase();
  const isGovernanceApprover = isAttendanceGovernanceApprover(user);
  const isCrmAdmin = normalizedRole === 'crm_admin';
  const isSalesDirector = normalizedRole === 'sales_director';
  const isSalesManager = normalizedRole === 'sales_manager';
  const isSalesRepresentative = normalizedRole === 'sales_rep';
  const isDepartmentHead = normalizedRole === 'department_head';
  const isPeopleManager = normalizedRole === 'people_manager';
  const isTeamLead = normalizedRole === 'team_lead';
  const managedTeamCount = user?.managedTeamIds?.length ?? 0;
  const teamCount = user?.teamIds?.length ?? 0;

  const permissionSummary =
    permissions.length > 8
      ? {
          label: `${permissions.length} permissions`,
          shouldList: false,
          preview: [],
        }
      : {
          label: `${permissions.length} permission${permissions.length === 1 ? '' : 's'}`,
          shouldList: permissions.length > 0,
          preview: permissions,
        };

  let scopeLabel = 'Self-service';
  let scopeDescription = 'Access is limited to your own employee record and assigned tasks.';

  if (isPlatformAdmin) {
    scopeLabel = 'Platform administration';
    scopeDescription = 'Access follows explicit global platform permissions.';
  } else if (isTenantAdmin) {
    scopeLabel = 'Organization admin';
    scopeDescription = `Access follows explicit organization administrator permissions for ${user?.tenantName ?? 'this organization'}.`;
  } else if (isCrmAdmin) {
    scopeLabel = 'Tenant CRM oversight';
    scopeDescription =
      'CRM access is tenant-wide through the CRM Administrator role. This role does not make you a sales record owner.';
  } else if (isSalesDirector) {
    scopeLabel = 'Assigned CRM department';
    scopeDescription = `CRM access follows the department target on your Sales Director role assignment.${user?.departmentName ? ` ${user.departmentName} is your HR home department.` : ''}`;
  } else if (isSalesManager) {
    scopeLabel = 'Assigned CRM population';
    scopeDescription = `CRM access follows the team or department target on your Sales Manager role assignment.${user?.departmentName ? ` ${user.departmentName} is your HR home department.` : ''}`;
  } else if (isSalesRepresentative) {
    scopeLabel = 'Own CRM records';
    scopeDescription =
      'CRM access is limited to records assigned to you. Your HR home department is shown separately.';
  } else if (isGovernanceApprover) {
    scopeLabel = 'Assigned governance';
    scopeDescription = 'Access is limited to attendance requests explicitly routed to you.';
  } else if (isDepartmentHead || user?.managedDepartmentId) {
    scopeLabel = 'Department leadership';
    scopeDescription = `Access follows the ${user?.departmentName ?? 'assigned'} department structure.`;
  } else if (isPeopleManager) {
    scopeLabel = 'Direct reports';
    scopeDescription =
      'Access follows formal reporting-manager assignments from organization setup.';
  } else if (isTeamLead) {
    scopeLabel =
      managedTeamCount > 0
        ? `${managedTeamCount} managed team${managedTeamCount === 1 ? '' : 's'}`
        : 'Led teams';
    scopeDescription = 'Access follows formal team-lead assignments from organization setup.';
  } else if (managedTeamCount > 0) {
    scopeLabel = `${managedTeamCount} managed team${managedTeamCount === 1 ? '' : 's'}`;
    scopeDescription = 'Access follows team-lead assignments from organization setup.';
  } else if (teamCount > 0) {
    scopeLabel = `${teamCount} team${teamCount === 1 ? '' : 's'}`;
    scopeDescription = 'Access follows team membership from organization setup.';
  } else if (user?.departmentName) {
    scopeLabel = `${user.departmentName} department`;
    scopeDescription = 'Access follows department assignment from organization setup.';
  }

  return {
    roleLabel: getRoleDisplayName(user?.role),
    departmentLabel:
      user?.departmentName ??
      (isGovernanceApprover && !user?.employeeId ? 'Not applicable' : 'Department not assigned'),
    positionLabel:
      user?.positionTitle ??
      (isGovernanceApprover && !user?.employeeId ? 'Not applicable' : 'Position not assigned'),
    scopeLabel,
    scopeDescription,
    permissionSummary,
  };
}
